import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { motion } from "framer-motion";
import { LEARN } from "../../theme";
import { useBreakpoint } from "../../shell/LearnShell";
import { ConfettiBurst } from "../../feedback/Celebration";
import {
  CheckIcon,
  PlusIcon,
  PopIn,
  RoundCounter,
  ShakeWrap,
  Tile,
  gameShell,
  seededShuffle,
  useCompleteOnce,
  useLetterAccent,
  useWrongFlash,
} from "./engineUtils";

function byWord(word, wordBank) {
  if (!word) return null;
  const w = String(word).toLowerCase();
  return (wordBank || []).find((x) => String(x.word).toLowerCase() === w) || { word };
}

function resolveRef(ref, wordBank) {
  if (!ref || !wordBank?.length) return null;
  // e.g. LWordCatalog.initialLWords[0] / distractorWords[1]
  const m = String(ref).match(/\[(\d+)\]/);
  if (!m) return null;
  const idx = Number(m[1]);
  const isDistractor = /distractor/i.test(ref);
  const pool = isDistractor
    ? wordBank.filter((w) => w.isTarget === false)
    : wordBank.filter((w) => w.isTarget !== false);
  return pool[idx] || wordBank[idx] || null;
}

function normalizePair(p, wordBank) {
  const first = p.first || byWord(p.firstWord, wordBank) || resolveRef(p.firstRef, wordBank);
  const second = p.second || byWord(p.secondWord, wordBank) || resolveRef(p.secondRef, wordBank);
  return {
    id: p.id || `${first?.word}-${second?.word}`,
    first,
    second,
    bothTarget: p.bothTarget !== false,
  };
}

/**
 * Sound-pair detective: every card shows two pictures. Stamp the cards where
 * BOTH pictures start with the letter sound; cards with a stray sound are
 * wrong. Completes when every bothTarget pair is stamped.
 */
export default function SoundPairsGame({ letter, config, speak, sfx, onComplete, onWrong, reduceMotion }) {
  const finish = useCompleteOnce(onComplete);
  const { wrongId, flash } = useWrongFlash();
  const bp = useBreakpoint();
  const compact = bp === "xs";
  const { accent, deep } = useLetterAccent(letter);
  const pairs = useMemo(
    () => seededShuffle((config.pairs || []).map((p) => normalizePair(p, config.wordBank)).filter((p) => p.first && p.second), 63),
    [config.pairs, config.wordBank]
  );
  const targetTotal = pairs.filter((p) => p.bothTarget).length;
  const [found, setFound] = useState(() => new Set());
  const [burst, setBurst] = useState(0);
  const timers = useRef([]);

  const later = useCallback((fn, ms) => { const t = setTimeout(fn, ms); timers.current.push(t); return t; }, []);
  useEffect(() => () => timers.current.forEach(clearTimeout), []);

  useEffect(() => {
    if (targetTotal > 0 && found.size >= targetTotal) {
      sfx?.("correct");
      setBurst((b) => b + 1);
      const t = setTimeout(finish, reduceMotion ? 0 : 700);
      return () => clearTimeout(t);
    }
    return undefined;
  }, [found, targetTotal, finish, reduceMotion, sfx]);

  const onTap = (pair) => {
    if (found.has(pair.id)) return;
    speak?.(`${pair.first.word}, ${pair.second.word}`, { interrupt: true });
    if (!pair.bothTarget) {
      flash(pair.id);
      onWrong?.();
      return;
    }
    sfx?.("snap");
    setFound((s) => new Set(s).add(pair.id));
    later(() => sfx?.("sparkle", { volume: 0.5 }), 180);
  };

  const cols = pairs.length <= 4 ? 2 : compact ? 2 : 3;
  const cardW = `min(calc(var(--tile) * 2.5), calc((var(--arena-w) - 40px) / ${cols} - ${compact ? 10 : 16}px))`;

  return (
    <div style={{ ...gameShell.root, gap: compact ? 10 : 14 }}>
      <ConfettiBurst key={burst} active={burst > 0} originY={35} reduceMotion={reduceMotion} />
      <RoundCounter done={found.size} total={targetTotal} accent={accent} />
      <div
        style={{
          flex: 1, minHeight: 0, overflowY: "auto",
          display: "grid",
          gridTemplateColumns: `repeat(${cols}, ${cardW})`,
          gap: compact ? 10 : 16, justifyContent: "center", alignContent: "center",
          padding: 4,
          paddingTop: compact ? 30 : 4,
        }}
        aria-label="Picture pairs"
      >
        {pairs.map((pair, i) => {
          const done = found.has(pair.id);
          return (
            <PopIn key={pair.id} index={i} reduceMotion={reduceMotion} style={{ width: "100%" }}>
              <ShakeWrap shake={wrongId === pair.id} reduceMotion={reduceMotion} style={{ borderRadius: LEARN.radius.lg, width: "100%" }}>
                <motion.button
                  type="button"
                  className="learn-piece"
                  aria-label={`${pair.first.word} and ${pair.second.word}`}
                  aria-pressed={done}
                  onClick={() => onTap(pair)}
                  whileTap={reduceMotion || done ? undefined : { scale: 0.96 }}
                  animate={done && !reduceMotion ? { scale: [1, 1.06, 1] } : { scale: 1 }}
                  transition={{ duration: 0.35 }}
                  style={{
                    width: "100%",
                    position: "relative",
                    display: "grid", gridTemplateColumns: "minmax(0, 1fr) auto minmax(0, 1fr)", alignItems: "center", gap: 6,
                    padding: compact ? 8 : 12,
                    background: done ? `${LEARN.color.mint}2b` : "rgba(255,255,255,0.92)",
                    backdropFilter: "blur(8px)", WebkitBackdropFilter: "blur(8px)",
                    border: `3px solid ${done ? LEARN.color.mint : LEARN.color.line}`,
                    borderRadius: LEARN.radius.lg,
                    boxShadow: done ? `0 0 0 4px ${LEARN.color.mint}55, ${LEARN.shadow.card}` : LEARN.shadow.card,
                    cursor: done ? "default" : "pointer",
                    transition: "background 200ms ease, border-color 200ms ease, box-shadow 200ms ease",
                    fontFamily: LEARN.font,
                  }}
                >
                  <Tile letter={letter} assetName={pair.first.assetName} alt={pair.first.word} fill lift={false} style={{ boxShadow: "none", border: "none", background: "transparent" }} />
                  <span aria-hidden="true" style={{ width: 30, height: 30, borderRadius: "50%", background: done ? `${LEARN.color.mint}33` : `${accent}18`, display: "grid", placeItems: "center", transition: "background 200ms ease" }}>
                    <PlusIcon size={18} color={done ? LEARN.color.mintDeep : deep} />
                  </span>
                  <Tile letter={letter} assetName={pair.second.assetName} alt={pair.second.word} fill lift={false} style={{ boxShadow: "none", border: "none", background: "transparent" }} />
                  {done && (
                    <motion.span
                      initial={reduceMotion ? false : { scale: 0, rotate: -20 }}
                      animate={{ scale: 1, rotate: -8 }}
                      transition={{ type: "spring", stiffness: 500, damping: 18 }}
                      style={{
                        position: "absolute", right: -8, top: -8, width: 36, height: 36, borderRadius: "50%",
                        background: LEARN.color.mint, display: "grid", placeItems: "center",
                        boxShadow: LEARN.shadow.sticker(LEARN.color.mintDeep),
                      }}
                    >
                      <CheckIcon size={20} />
                    </motion.span>
                  )}
                </motion.button>
              </ShakeWrap>
            </PopIn>
          );
        })}
      </div>
    </div>
  );
}
